import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import BingoCard from "../subcomponents/BingoCard";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import QRCode from "qrcode.react";
import { debounce } from "lodash";

const CardGenerator = React.memo(() => {
  const [generatedCards, setGeneratedCards] = useState([]);
  const [numberOfCards, setNumberOfCards] = useState(null);
  const [blackWhite, setBlackWhite] = useState(false);
  const [branch, setBranch] = useState(null);
  const [startingPoint, setStartingPoint] = useState(null);
  const [branches, setBranches] = useState([]);
  const [qrUrl, setQrUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [branchPage, setBranchPage] = useState(1);
  const [hasMoreBranches, setHasMoreBranches] = useState(true);
  const qrRef = useRef(null);

  const { currentUser } = useSelector((state) => state.user);

  // Memoized fetchBranches function with pagination
  const fetchBranches = useCallback(async (page = 1, isLoadMore = false) => {
    try {
      setBranchLoading(true);
      setError(null);
      
      let response;
      const limit = 50;
      const params = new URLSearchParams({ page, limit }).toString();
      
      if (currentUser?.role === "superadmin") {
        response = await fetch(`/api/branch/branch?${params}`);
      } else {
        response = await fetch(`/api/branch/getbranch/${currentUser?.username}?${params}`);
      }
      
      if (!response.ok) throw new Error("Failed to fetch branches");
      
      const data = await response.json();
      const branchesData = data?.branches || data || [];
      
      if (isLoadMore) {
        setBranches(prev => [...prev, ...branchesData]);
      } else {
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      }
      
      setHasMoreBranches(branchesData.length >= limit);
      setBranchPage(page);
    } catch (err) {
      console.error("Branch fetch error:", err);
      setError("Error fetching branches");
      setBranches([]);
    } finally {
      setBranchLoading(false);
    }
  }, [currentUser]);

  const loadMoreBranches = () => {
    fetchBranches(branchPage + 1, true);
  };

  useEffect(() => {
    fetchBranches(1);
  }, [fetchBranches]);

  // Memoized bingo number generator
  const generateBingoNumbers = useCallback(() => {
    const letters = ["B", "I", "N", "G", "O"];
    const numbers = {};
    let count = 1;
    
    letters.forEach((letter) => {
      numbers[letter] = [];
      for (let i = 1; i <= 15; i++) {
        numbers[letter].push(count);
        count++;
      }
    });
    return numbers;
  }, []);

  // Memoized card generator
  const generateCard = useCallback(() => {
    const numbers = generateBingoNumbers();
    const card = {};

    Object.keys(numbers).forEach((letter, index) => {
      const chosenNumbers = [];
      for (let i = 0; i < 5; i++) {
        if (index === 2 && i === 2) {
          chosenNumbers.push("Free");
        } else {
          const randomIndex = Math.floor(Math.random() * numbers[letter].length);
          chosenNumbers.push(numbers[letter].splice(randomIndex, 1)[0]);
        }
      }
      card[letter] = chosenNumbers;
    });
    return card;
  }, [generateBingoNumbers]);

  // Debounced card generation handler
  const handleGenerateCards = useCallback(debounce(async () => {
    if (generateButtonDisabled()) return;

    try {
      setLoading(true);
      setError(null);
      
      const cards = Array.from({ length: numberOfCards }, (_, i) => ({
        id: startingPoint + i,
        card: generateCard()
      }));

      setGeneratedCards(cards);

      // Save generated cards to backend
      const response = await axios.post("/api/card/cards", {
        branch: branch,
        cards: cards.map((card) => ({ id: card.id, card: card.card })),
      });
      
      alert(response.data?.message || "Cards saved successfully!");
    } catch (error) {
      console.error("Save error:", error);
      setError(error.response?.data?.message || "Failed to save cards");
    } finally {
      setLoading(false);
    }
  }, 300), [numberOfCards, startingPoint, branch, generateCard]);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      handleGenerateCards.cancel();
    };
  }, [handleGenerateCards]);

  const generateQRCode = useCallback(() => {
    if (!branch) {
      setError("Please select a branch first");
      return;
    }
    const encodedBranch = encodeURIComponent(branch);
    const url = `https://lotterybingoet.com/bingo?branch=${encodedBranch}`;
    setQrUrl(url);
  }, [branch]);

  const downloadQRCode = useCallback(() => {
    if (!qrRef.current) return;
    
    const canvas = qrRef.current.querySelector("canvas");
    if (!canvas) return;
    
    const pngUrl = canvas.toDataURL("image/png");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `QRCode_${branch}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }, [branch]);

  const generateButtonDisabled = useCallback(() => {
    return !numberOfCards || !branch || !startingPoint;
  }, [numberOfCards, branch, startingPoint]);

  // Memoized options for dropdowns
  const numberOfCardsOptions = React.useMemo(() => 
    Array.from({ length: 1001 }, (_, i) => ({
      value: i.toString(),
      label: i.toString()
    })), []);

  const startingPointOptions = React.useMemo(() => 
    Array.from({ length: 10000 }, (_, i) => ({
      value: (i + 1).toString(),
      label: (i + 1).toString()
    })), []);



  // Clean up generated cards on unmount
  useEffect(() => {
    return () => {
      setGeneratedCards([]);
    };
  }, []);

  return (
    <div className="tw-container tw-mx-auto tw-p-4">
      <div className="tw-flex tw-flex-col tw-gap-6">
        <div className="tw-flex tw-items-center tw-gap-4">
          <Link
            to="/card"
            className="tw-border-2 tw-p-1 tw-px-4 tw-border-blue-800 tw-text-blue-800 hover:tw-bg-blue-800 hover:tw-text-white tw-transition-colors tw-rounded"
          >
            Show Cards
          </Link>
          <h1 className="tw-text-2xl tw-font-bold">Card Generator</h1>
        </div>

        {loading && <div className="tw-text-center">Loading...</div>}
        {error && <div className="tw-text-red-500 tw-text-center">{error}</div>}

        <div className="tw-bg-gray-100 tw-rounded-lg tw-p-6 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
          <div>
            <label className="tw-block tw-mb-2 tw-font-medium">Number of Cards</label>
            <select
              className="tw-w-full tw-p-2 tw-border tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
              onChange={(e) => setNumberOfCards(parseInt(e.target.value))}
              value={numberOfCards || ""}
            >
              <option value="">Select</option>
              {numberOfCardsOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="tw-relative">
            <label className="tw-block tw-mb-2 tw-font-medium">Branch</label>
            <select
              className="tw-w-full tw-p-2 tw-border tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
              onChange={(e) => setBranch(e.target.value)}
              value={branch || ""}
            >
              <option value="">Select</option>
              {Array.isArray(branches) && branches.map((branch) => (
                <option key={branch._id || branch.id} value={branch.name}>
                  {branch.name}
                </option>
              ))}
            </select>
            {hasMoreBranches && (
              <button
                onClick={loadMoreBranches}
                disabled={branchLoading}
                className="tw-absolute tw-right-0 tw-bottom-0 tw-mb-1 tw-mr-1 tw-px-2 tw-py-1 tw-bg-blue-600 tw-text-white tw-text-xs tw-rounded hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {branchLoading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </div>

          <div>
            <label className="tw-block tw-mb-2 tw-font-medium">Starting Point</label>
            <select
              className="tw-w-full tw-p-2 tw-border tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
              onChange={(e) => setStartingPoint(parseInt(e.target.value))}
              value={startingPoint || ""}
            >
              <option value="">Select</option>
              {startingPointOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <input
              type="checkbox"
              id="blackWhite"
              checked={blackWhite}
              onChange={(e) => setBlackWhite(e.target.checked)}
              className="tw-w-4 tw-h-4 tw-text-blue-600 tw-rounded focus:tw-ring-blue-500"
            />
            <label htmlFor="blackWhite">Black & White</label>
          </div>

          <div className="tw-col-span-full tw-flex tw-gap-4">
            <button
              onClick={handleGenerateCards}
              disabled={generateButtonDisabled() || loading}
              className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              {loading ? "Generating..." : "Generate Cards"}
            </button>

            <button
              onClick={generateQRCode}
              disabled={!branch}
              className="tw-bg-green-600 tw-text-white tw-px-4 tw-py-2 tw-rounded hover:tw-bg-green-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              Generate QR Code
            </button>
          </div>
        </div>

        {qrUrl && (
          <div className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6 tw-flex tw-flex-col tw-items-center tw-gap-4">
            <div ref={qrRef}>
              <QRCode value={qrUrl} size={256} />
            </div>
            <button
              onClick={downloadQRCode}
              className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded hover:tw-bg-blue-700 tw-transition-colors"
            >
              Download QR Code
            </button>
          </div>
        )}

        {generatedCards.length > 0 && (
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
            {generatedCards.map((card) => (
              <div key={card.id} className="tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4">
                <BingoCard card={card.card} />
                <div className="tw-text-center tw-mt-2 tw-font-medium">
                  ID: {card.id} | Branch: {branch}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default CardGenerator;